import { t } from "./t";

/**
 * Fable F3/F6: shared between `ConnectionsScreen` (QBO/FreeAgent/Sage BC/LuciBook land here on
 * `?error=oauth_denied&system=<system>&oauth_error=<code>`) and `XeroReturn` (Xero's own per-vendor return
 * page, always `system="xero"`) so the two never drift into two different vendor-attribution or error-code
 * stories. `oauthCopy.ts` lives under `i18n/`, not either screen's own directory, because it calls `t()`
 * with the literal keys `tests/i18n.test.ts`'s "every key is referenced" check greps for — moving it under
 * a screen directory would still satisfy that grep (it scans every `.ts`/`.tsx` under `src/`), but this is
 * the copy-composition layer, not a screen, so it belongs beside `t.ts` and `en.ts`.
 */

/**
 * `connections.py::_denied_query`'s `system` values, and the name each renders as.
 *
 * Round 3 (Opus verification review, MEDIUM): exported and reused by `ConnectionsScreen`/`ConnectScreen`'s
 * own `revoke.revoke_failed`/`connections.revoke_failed_detail` rendering, which used to hardcode "Xero"
 * regardless of `ConnectionOut.system` - the one thing this function already existed to get right.
 */
export function vendorName(system: string | null): string {
  switch (system) {
    case "xero":
      return t("connections.xero_name");
    case "qbo":
      return t("connections.qbo_name");
    case "freeagent":
      return t("connections.freeagent_name");
    case "sage_bc":
      return t("connections.sagebc_name");
    case "hmrc":
      return t("connections.hmrc_name");
    case "lucibook":
      return t("connections.lucibook_name");
    default:
      // Defensive only: every current denial redirect sets `system` (F3). A future one that forgets to
      // still gets a sentence that reads correctly, just without naming a vendor.
      return t("oauth.denied_vendor_fallback");
  }
}

/**
 * Round 6 (Opus verification review of round 5, LOW): who holds the access described in "remove
 * {holder}'s access directly in {vendor}" copy. Mirrors `workflow/registry.py::revoke_access_holder_name`
 * exactly (kept in sync by hand, same as every other Python/TypeScript duplication in this file) - a
 * `lucibook` row IS the vendor, so LuciBook cannot be the thing holding access to itself; `LuciMigration`
 * is. Every other system is a third party LuciBook connects to, so LuciBook genuinely holds the access
 * there.
 */
export function revokeAccessHolderName(system: string | null): string {
  return system === "lucibook" ? "LuciMigration" : "LuciBook";
}

/**
 * `connections.py::OAUTH_ERROR_CODES`'s whitelist, mirrored: any `oauth_error` outside it (or `null`,
 * the sage_bc "no business returned" case) reads as `oauth.denied.unknown`.
 */
export function oauthDeniedMessage(system: string | null, oauthError: string | null): string {
  const vendor = vendorName(system);
  switch (oauthError) {
    case "access_denied":
      return t("oauth.denied.access_denied", { system: vendor });
    case "invalid_scope":
      return t("oauth.denied.invalid_scope", { system: vendor });
    case "invalid_request":
      return t("oauth.denied.invalid_request", { system: vendor });
    case "unauthorized_client":
      return t("oauth.denied.unauthorized_client", { system: vendor });
    case "redirect_uri_mismatch":
      return t("oauth.denied.redirect_uri_mismatch", { system: vendor });
    case "server_error":
      return t("oauth.denied.server_error", { system: vendor });
    case "temporarily_unavailable":
      return t("oauth.denied.temporarily_unavailable", { system: vendor });
    default:
      return t("oauth.denied.unknown", { system: vendor });
  }
}
